import {
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";

function LegacyMerchantDomainViewRedirect() {
  const { publicDomain } = useParams();
  const { search } = useLocation();
  const domain = publicDomain ?? "";
  return (
    <Navigate
      replace
      to={`/onboardings/legacy/merchants/view/domain/${encodeURIComponent(domain)}${search}`}
    />
  );
}

function OnboardingV2UrlRedirect() {
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const match = pathname.match(/^\/onboardings\/v2(?:\/(.*))?$/);
  const tail = (match?.[1] ?? "").replace(/\/$/, "");
  if (!tail || tail === "merchants") {
    return <Navigate replace to={`/onboardings/merchants${search}`} />;
  }
  if (tail.startsWith("merchants/")) {
    return <Navigate replace to={`/onboardings/${tail}${search}`} />;
  }
  return <Navigate replace to={`/onboardings/merchants${search}`} />;
}

function LegacyOmsAnalyticsRedirect() {
  const { tab } = useParams();
  const { search } = useLocation();
  const suffix = tab ? `/${tab}` : "";
  return (
    <Navigate
      replace
      to={`/onboardings/analytics/order-editing${suffix}${search}`}
    />
  );
}

export const onboardingsRoutes: RouteObject[] = [
  {
    handle: { breadcrumb: "Onboardings" },
    async lazy() {
      const { Page } = await import("../app/page");
      return { element: <Page hidePadding={() => true} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="merchants" /> },
      { path: "v2", element: <OnboardingV2UrlRedirect /> },
      { path: "v2/*", element: <OnboardingV2UrlRedirect /> },
      {
        path: "legacy",
        handle: { breadcrumb: "Legacy" },
        children: [
          { index: true, element: <Navigate replace to="merchants" /> },
          {
            path: "merchants",
            handle: { breadcrumb: "Merchants" },
            children: [
              { index: true, element: <Navigate replace to="all" /> },
              {
                path: "view/:merchantId",
                handle: { breadcrumb: "Details" },
                async lazy() {
                  const { MerchantDetailPage } = await import(
                    "./merchants/MerchantDetailPage"
                  );
                  return { Component: MerchantDetailPage };
                },
              },
              {
                path: "view/domain/:publicDomain",
                handle: { breadcrumb: "Configuration Details" },
                async lazy() {
                  const { MerchantDetailPage } = await import(
                    "./merchants/MerchantDetailPage"
                  );
                  return { Component: MerchantDetailPage };
                },
              },
              {
                path: ":tab",
                async lazy() {
                  const { MerchantsPage } = await import(
                    "./merchants/MerchantsPage"
                  );
                  return { Component: MerchantsPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "merchants",
        handle: { breadcrumb: "Merchants" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: "view/domain/:publicDomain",
            element: <LegacyMerchantDomainViewRedirect />,
          },
          {
            path: "view/:teamId",
            handle: { breadcrumb: "Details" },
            async lazy() {
              const { V2MerchantDetailPage } = await import(
                "./v2/V2MerchantDetailPage"
              );
              return { Component: V2MerchantDetailPage };
            },
          },
          {
            path: ":tab",
            async lazy() {
              const { V2MerchantsPage } = await import("./v2/V2MerchantsPage");
              return { Component: V2MerchantsPage };
            },
          },
        ],
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        children: [
          { index: true, element: <Navigate replace to="overview" /> },
          {
            path: "overview",
            handle: { breadcrumb: "Overview" },
            async lazy() {
              const { OverviewPage } = await import(
                "./analytics/overview/OverviewPage"
              );
              return { Component: OverviewPage };
            },
          },
          {
            path: "returns",
            handle: { breadcrumb: "Returns" },
            children: [
              { index: true, element: <Navigate replace to="overview" /> },
              {
                path: ":tab",
                async lazy() {
                  const { ReturnsAnalyticsPage } = await import(
                    "./analytics/returns/ReturnsAnalyticsPage"
                  );
                  return { Component: ReturnsAnalyticsPage };
                },
              },
            ],
          },
          {
            path: "order-editing",
            handle: { breadcrumb: "Order Editing" },
            children: [
              { index: true, element: <Navigate replace to="overview" /> },
              {
                path: ":tab",
                async lazy() {
                  const { OmsAnalyticsPage } = await import(
                    "./analytics/oms/OmsAnalyticsPage"
                  );
                  return { Component: OmsAnalyticsPage };
                },
              },
            ],
          },
          {
            path: "oms",
            children: [
              { index: true, element: <LegacyOmsAnalyticsRedirect /> },
              { path: ":tab", element: <LegacyOmsAnalyticsRedirect /> },
            ],
          },
        ],
      },
      {
        path: "configurations",
        handle: { breadcrumb: "Configurations" },
        children: [
          { index: true, element: <Navigate replace to="edit" /> },
          {
            path: "edit",
            async lazy() {
              const { ConfigurationsPage } = await import(
                "./configurations/ConfigurationsPage"
              );
              return { Component: ConfigurationsPage };
            },
          },
          {
            path: "all/:merchantId?/:product?",
            async lazy() {
              const { ConfigurationsPage } = await import(
                "./configurations/ConfigurationsPage"
              );
              return { Component: ConfigurationsPage };
            },
          },
        ],
      },
      {
        path: "leaderboard/:product?/:merchantId?",
        handle: { breadcrumb: "Leaderboard" },
        async lazy() {
          const { MyOnboardingsPage } = await import(
            "./my-onboardings/MyOnboardingsPage"
          );
          return { Component: MyOnboardingsPage };
        },
      },
    ],
  },
];
